import { useNavigate, useParams } from 'react-router-dom';

import { BlockConfirmButton, ErrorState, LoadingState, PagePlate } from '../../components/index.js';
import { useCurrentUser } from '../../lib/auth.js';
import { useBlockUser } from '../../lib/blocks.js';
import { getErrorMessage } from '../../lib/errors.js';
import { useGameCatalog } from '../../lib/games.js';
import { useUserProfile } from '../../lib/users.js';
import { GameplaySection, ProfileHero } from './components/index.js';

/**
 * The public profile screen (APP_SPEC §13, §13.2, §13.3; docs/APP_PLAN.md
 * §12 M4). One unified profile hero (framed avatar, identity, selected
 * profile title, overall stats, decorative side art) followed by a
 * "Gameplay" section of reusable per-game cards. Username casing is exactly
 * what the server returns (APP_SPEC §9); per-game statistics render
 * generically from each game's own `statisticDescriptors` (APP_SPEC §43).
 * The response never carries an email (docs/APP_PLAN.md §9.8).
 *
 * There is exactly one profile rendering path — a user sees their own
 * profile exactly as anyone else does — so the only viewer-dependent piece
 * is the block action, hidden on your own profile.
 */
export function ProfilePage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const profile = useUserProfile(username);
  const catalog = useGameCatalog();
  const currentUser = useCurrentUser();
  const blockUser = useBlockUser();

  if (!username) {
    return <ErrorState message="No user was specified." />;
  }

  if (profile.isPending) {
    return <LoadingState label="Loading profile…" />;
  }

  if (profile.isError) {
    return (
      <ErrorState message={getErrorMessage(profile.error)} onRetry={() => void profile.refetch()} />
    );
  }

  const { user, statistics, gameStatistics, customization } = profile.data;
  const isOwnProfile = currentUser.data?.id === user.id;

  return (
    <PagePlate>
      <ProfileHero
        userId={user.id}
        username={user.username}
        joinedAt={user.joinedAt}
        avatar={user.avatar}
        statistics={statistics}
        customization={customization}
      />

      <GameplaySection gameStatistics={gameStatistics} catalog={catalog.data ?? []} />

      {!isOwnProfile && (
        <div className="flex flex-col items-center gap-3">
          {blockUser.isError && <ErrorState message={getErrorMessage(blockUser.error)} />}
          <BlockConfirmButton
            username={user.username}
            isLoading={blockUser.isPending}
            onConfirm={() =>
              // `mutateAsync` so the dialog stays open (and un-resubmittable)
              // until the block is recorded, and only then navigates away.
              blockUser.mutateAsync(user.username).then(() => navigate('/friends'))
            }
          />
        </div>
      )}
    </PagePlate>
  );
}
